import type { NormalizedMarketRow } from '@/types/market';
import type { BenchmarkStats, ScoreBand } from '@/types/insights';
import { clamp, zScore } from '@/utils/numbers';
import { scoringWeights } from '@/config/scoringWeights';
import { magnitudeBand } from './bands';

/**
 * opportunityScore: 0..100, higher = more buyer opportunity relative to the
 * rest of the map right now (more price cuts, more inventory, longer DOM --
 * i.e. the inverse ingredients of hotColdScore, framed for a buyer's-agent
 * use case rather than a general temperature read).
 */
export function computeOpportunityScore(row: NormalizedMarketRow, benchmark: BenchmarkStats): ScoreBand {
  const w = scoringWeights.opportunity;

  const zCut = zScore(
    row.priceReductionSharePct,
    benchmark.meanByField.priceReductionSharePct,
    benchmark.stdByField.priceReductionSharePct,
  );
  const zMoi = zScore(row.monthsOfInventory, benchmark.meanByField.monthsOfInventory, benchmark.stdByField.monthsOfInventory);
  const zDom = zScore(row.medianDaysOnMarket, benchmark.meanByField.medianDaysOnMarket, benchmark.stdByField.medianDaysOnMarket);

  const parts: number[] = [];
  if (zCut !== undefined) parts.push(zCut * w.priceCutZWeight);
  if (zMoi !== undefined) parts.push(zMoi * w.moiZWeight);
  if (zDom !== undefined) parts.push(zDom * w.domZWeight);

  if (parts.length === 0) return magnitudeBand(undefined);

  const composite = parts.reduce((a, b) => a + b, 0);
  const scaled = clamp(w.center + composite * w.scaleToRange, 0, 100);
  return magnitudeBand(scaled);
}
